import type { LinkProps as NextLinkProps } from 'next/link';

import * as React from 'react';
import styled from '@emotion/styled';
import { useTheme } from '@mui/material/styles';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import { useHighlightHandlers } from '../../contexts/HighlightContext/Highlight';
import { Icon, Typography as MuiTypography } from '@mui/material';
import { Link } from '../Link';
import Image from 'next/image';

interface NavigationMenuItemProps {
  href: NextLinkProps['href'];
  title: string;
  dense?: boolean;
  icon?: any | undefined;
  onClick?: Function;
}

interface ListItemIconStyledProps {
  width: number;
  height: number;
}

const LinkStyled = styled(Link)(({ theme }) => ({
  display: 'flex',
  color: theme.theme_component_navigation_menu_c_item_color || theme.palette.primary.contrastText,
  '&:hover': {
    backgroundColor: theme.theme_component_navigation_menu_c_item_hover_background_color || theme.palette.action.hover,
    color: theme.theme_component_navigation_menu_c_item_hover_color || undefined,
  },
  '&:active': {
    backgroundColor:
      theme.theme_component_navigation_menu_item_c_active_background_color || theme.palette.action.active,
    color: theme.theme_component_navigation_menu_c_item_active_color || undefined,
  },
}));
const ListItemIconStyled = styled(ListItemIcon)<ListItemIconStyledProps>(({ theme, width, height }) => ({
  marginRight: theme.spacing(1),
  marginBottom: theme.spacing(2),
  minWidth: 'unset',
  width: width,
  height: height,
  '& > span': {
    flex: 1,
  },
}));

const Typography = React.forwardRef<HTMLInputElement, any>((props, ref) =>
  useHighlightHandlers(MuiTypography, { forwardedRef: ref, ...props })
);

export const NavigationMenuItem = (props: NavigationMenuItemProps) => {
  const { href, title, icon, dense } = props;
  const theme = useTheme();
  const iconColorStyle = theme.palette.primary.contrastText;
  const subtitle1Ref = React.useRef<HTMLInputElement | null>(null);
  const Icon = typeof icon?.[0] === 'function' ? icon[0] : undefined;
  return (
    <LinkStyled href={href} title={title} underline="none">
      <ListItemButton dense={dense}>
        {Icon && (
          <ListItemIconStyled width={26} height={26}>
            {Icon ? (
              <Icon color={iconColorStyle} />
            ) : (
              <Image src={icon?.[0]} alt={title || 'image'} width={26} height={26} />
            )}
          </ListItemIconStyled>
        )}
        <ListItemText
          primary={
            <Typography ref={subtitle1Ref} variant="subtitle1" component="span" noWrap>
              {title}
            </Typography>
          }
        />
      </ListItemButton>
    </LinkStyled>
  );
};
